import { Divider, Switch, Text } from '@/shared/ui/primitives';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import { TEST_IDS } from '@/testIds';
import { EditableSectionCard, FieldError, NumberStepper } from '@/shared/ui';
import {
  validatePassRemainingCredits,
  validatePassTotalCredits,
  type PassFormValues,
} from '../../model';
import { CreditBalanceStepper, type ActiveCredit } from './CreditBalanceStepper';
import styles from './PassCreditsField.module.css';

export type PassCreditsMode = 'total' | 'balance';

interface PassCreditsFieldProps {
  mode: PassCreditsMode;
}

const CREDIT_FIELD_NAMES = ['remainingCredits', 'totalCredits'] as const;
const SET_OPTIONS = {
  shouldDirty: true,
  shouldTouch: true,
  shouldValidate: true,
} as const;

const parseCredits = (value: string): number => {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : 0;
};

export const PassCreditsField: FC<PassCreditsFieldProps> = ({ mode }) => {
  const {
    control,
    getValues,
    setValue,
    trigger,
    formState: { errors, submitCount },
  } = useFormContext<PassFormValues>();
  const isUnlimited = Boolean(useWatch({ control, name: 'isUnlimitedCredits' }));
  const isBalance = mode === 'balance';
  const getIsUnlimited = (): boolean => Boolean(getValues('isUnlimitedCredits'));

  const { field: remainingField } = useController({
    name: 'remainingCredits',
    control,
    rules: {
      validate: (value) =>
        !isBalance ||
        validatePassRemainingCredits(value, getIsUnlimited(), getValues('totalCredits')),
    },
  });
  const { field: totalField } = useController({
    name: 'totalCredits',
    control,
    rules: {
      validate: (value) => validatePassTotalCredits(value, getIsUnlimited()),
    },
  });

  const remaining = remainingField.value ?? '';
  const total = totalField.value ?? '';
  const bindRemainingField = remainingField.ref;
  const bindTotalField = totalField.ref;
  const parsedRemaining = parseCredits(remaining);
  const parsedTotal = parseCredits(total);
  const [activeCredit, setActiveCredit] = useState<ActiveCredit>('remaining');
  const [isExpanded, setIsExpanded] = useState(false);
  const remainingRef = useRef<HTMLInputElement | null>(null);
  const totalRef = useRef<HTMLInputElement | null>(null);
  const unlimitedToggleRef = useRef<HTMLInputElement>(null);
  const setRemainingRef = useCallback(
    (element: HTMLInputElement | null): void => {
      remainingRef.current = element;
      bindRemainingField(element);
    },
    [bindRemainingField]
  );
  const setTotalRef = useCallback(
    (element: HTMLInputElement | null): void => {
      totalRef.current = element;
      bindTotalField(element);
    },
    [bindTotalField]
  );
  const remainingError = isBalance ? errors.remainingCredits : undefined;
  const hasErrors = Boolean(remainingError || errors.totalCredits);

  useEffect(() => {
    if (submitCount > 0 && hasErrors) {
      setIsExpanded(true);
    }
  }, [hasErrors, submitCount]);

  const revalidateIfNeeded = (): void => {
    if (hasErrors) {
      void trigger(CREDIT_FIELD_NAMES);
    }
  };

  const setRemaining = (value: string, validate = false): void => {
    remainingField.onChange(value);
    if (validate) {
      void trigger(CREDIT_FIELD_NAMES);
    } else {
      revalidateIfNeeded();
    }
  };

  const setTotal = (value: string, validate = false): void => {
    totalField.onChange(value);
    if (!isBalance) {
      remainingField.onChange(value);
    }
    if (validate) {
      void trigger(CREDIT_FIELD_NAMES);
    } else {
      revalidateIfNeeded();
    }
  };

  const handleStep = (delta: number): void => {
    if (isUnlimited) return;

    if (!isBalance || activeCredit === 'total') {
      const minimum = isBalance ? Math.max(1, parsedRemaining) : 1;
      setTotal(String(Math.max(minimum, parsedTotal + delta)), true);
      return;
    }

    const nextRemaining = Math.max(0, Math.min(parsedTotal, parsedRemaining + delta));
    setRemaining(String(nextRemaining), true);
  };

  const handleUnlimitedChange = (next: boolean): void => {
    setValue('isUnlimitedCredits', next, SET_OPTIONS);
    if (!next && !isBalance) {
      setValue('remainingCredits', getValues('totalCredits'), SET_OPTIONS);
    }
    void trigger(CREDIT_FIELD_NAMES);
  };

  const decrementDisabled =
    activeCredit === 'remaining'
      ? parsedRemaining <= 0
      : parsedTotal <= Math.max(1, isBalance ? parsedRemaining : 1);
  const incrementDisabled =
    isBalance && activeCredit === 'remaining' && parsedRemaining >= parsedTotal;
  const summary = isUnlimited
    ? 'Unlimited'
    : isBalance
      ? `${remaining || '—'}/${total || '—'}`
      : total || '—';
  const errorMessage =
    remainingError?.message?.toString() ?? errors.totalCredits?.message?.toString();
  const balanceFocusRef = errors.totalCredits && !remainingError ? totalRef : remainingRef;

  return (
    <>
      <EditableSectionCard
        label="Credits"
        summary={summary}
        focusTargetRef={isUnlimited ? unlimitedToggleRef : isBalance ? balanceFocusRef : totalRef}
        invalid={hasErrors}
        triggerTestId={TEST_IDS.creditsEditorTrigger}
        expanded={isExpanded}
        onExpandedChange={setIsExpanded}
        onCollapse={() => setActiveCredit('remaining')}
      >
        {isBalance ? (
          <CreditBalanceStepper
            remaining={remaining}
            total={total}
            activeCredit={activeCredit}
            unlimited={isUnlimited}
            remainingInvalid={Boolean(remainingError)}
            totalInvalid={Boolean(errors.totalCredits)}
            decrementDisabled={decrementDisabled}
            incrementDisabled={incrementDisabled}
            remainingRef={setRemainingRef}
            totalRef={setTotalRef}
            onActiveCreditChange={setActiveCredit}
            onRemainingChange={setRemaining}
            onTotalChange={setTotal}
            onRemainingBlur={remainingField.onBlur}
            onTotalBlur={totalField.onBlur}
            onStep={handleStep}
          />
        ) : (
          <NumberStepper
            value={total}
            unlimited={isUnlimited}
            decrementDisabled={parsedTotal <= 1}
            invalid={Boolean(errors.totalCredits)}
            onInput={setTotal}
            onStep={handleStep}
            onBlur={totalField.onBlur}
            inputRef={setTotalRef}
            inputTestId={TEST_IDS.creditsInput}
            inputAriaLabel="Credits"
            decrementAriaLabel="Decrease credits"
            incrementAriaLabel="Increase credits"
            decrementTestId={TEST_IDS.passCreditsStepperDecrement}
            incrementTestId={TEST_IDS.passCreditsStepperIncrement}
          />
        )}

        <Divider />

        <div className={styles.toggleRow}>
          <Text weight="regular">Unlimited</Text>
          <Switch
            ref={unlimitedToggleRef}
            checked={isUnlimited}
            onChange={(event) => handleUnlimitedChange(event.target.checked)}
            aria-label="Toggle unlimited credits"
            data-testid={TEST_IDS.passUnlimitedCreditsToggle}
          />
        </div>
      </EditableSectionCard>
      <FieldError message={errorMessage} />
    </>
  );
};
